const _ = require("lodash");
const CanvasMath = require("../CanvasMath");
const ArrayUtil = require("../../util/ArrayUtil");
const Math2D = require("../../util/Math2D");
const ArrowDraw = require("../../util/ArrowDraw");
const colors = require("../../util/ColorUtil").getCurrentScheme();

const generatePath = function(start, gridSize, given) {
	const prefills = _.uniqBy(
		_.filter(given, g => g[0] !== 1),
		pre => pre[0]
	);
	const prefillCoords = _.map(prefills, p => _.slice(p, 1));

	const isOnBoard = crd => {
		return _.every(crd, (c, dir) => {
			return c >= 0 && c < gridSize[dir];
		});
	};
	const wasVisited = (coord, visited) => {
		return ArrayUtil.isElInArray(visited, coord);
	};
	const getNextMoves = (pathIndex, coord, visited) => {
		const allConnected = [
			[coord[0] + 1, coord[1]],
			[coord[0], coord[1] + 1],
			[coord[0] - 1, coord[1]],
			[coord[0], coord[1] - 1],
		];
		const prefillIndex = _.findIndex(prefills, p => p[0] === pathIndex + 2);
		const isNextIndexGiven = prefillIndex !== -1;
		const connected = [];
		_.each(allConnected, crd => {
			let valid = isOnBoard(crd) && !wasVisited(crd, visited);
			if (valid) {
				if (isNextIndexGiven) {
					valid = _.isEqual(crd, prefillCoords[prefillIndex]);
				} else if (ArrayUtil.isElInArray(prefillCoords, crd)) {
					const index = _.findIndex(prefillCoords, pc => _.isEqual(pc, crd));
					const pos = prefills[index][0];
					valid = pos - pathIndex - 1 === 1;
				}
			}
			if (valid) connected.push(crd);
		});
		return connected;
	};
	const nextSteps = function(pathIndex, coord, previousVisits) {
		const next = getNextMoves(pathIndex, coord, previousVisits);
		let segment = [];
		if (next.length) {
			_.each(next, crd => {
				const possiblePath = nextSteps(
					pathIndex + 1,
					crd,
					previousVisits.concat([crd])
				);
				if (possiblePath.length > segment.length) segment = possiblePath;
			});
		}
		return [coord].concat(segment);
	};
	return nextSteps(0, start, [start]);
};

// The aspect ratio of the given plot height/width will be maintained and the plot will be centered as necessary
// ctx: context to draw upon
// x, y: top left coordinates
// w, h: width and height
const drawCheckerBoardPaths = function(
	ctx,
	x,
	y,
	w,
	h,
	numCols,
	numRows,
	pathEndIn,
	prefillsIn,
	showEnd,
	drawPath
) {
	const numCells = numCols * numRows;
	const radiusRatio = drawPath ? 1.066 * 0.38 : 0;
	const pathTransparency = 0.6;
	const pathColor = colors.inputContrastFill;

	const pathStart = [0, 0];
	const pathEnd = [pathEndIn[0], numRows - pathEndIn[1] - 1];
	const grid = ArrayUtil.makeMatrix(numCols, numRows, "");

	const pref = _.map(prefillsIn, p => {
		return [p[0], p[1], numRows - p[2] - 1];
	});
	pref.unshift([1, ...pathStart]);
	if (showEnd && pathEnd && pathEnd.length) {
		pref.unshift([numCells, ...pathEnd]);
	}
	const prefills = _.uniqBy(pref, el => el[0]);

	const path = drawPath
		? generatePath(pathStart, [numCols, numRows], prefills)
		: [];

	_.each(path, (p, i) => {
		grid[p[1]][p[0]] = i + 1;
	});
	_.each(prefills, (p, i) => {
		grid[p[2]][p[1]] = p[0];
	});

	const boardMeasure = {
		// NEEDS TO BE GENERALIZED LATER FOR NON-SQUARE BOARDS
		x: x + (w * 0.2) / 2,
		y: y + (h * 0.2) / 2,
		w: w * 0.8,
		h: h * 0.8,
	};

	// NEEDS TO BE GENERALIZED LATER FOR NON-SQUARE BOARDS
	const squareSize = boardMeasure.w / numCols;

	const getSquareRect = function(i, j) {
		return {
			x: boardMeasure.x + i * squareSize,
			y: boardMeasure.y + j * squareSize,
			w: squareSize,
			h: squareSize,
		};
	};
	const getSquareCenter = function(i, j) {
		return {
			x: boardMeasure.x + (i + 0.5) * squareSize,
			y: boardMeasure.y + (j + 0.5) * squareSize,
		};
	};

	// Draw Board
	_.each(grid, (row, i) => {
		_.each(row, (c, j) => {
			const rect = getSquareRect(j, i);
			const center = getSquareCenter(j, i);
			const isStart = _.isEqual([i, j], pathStart);

			// Shade square
			ctx.save();
			let color;
			if ((i % 2 === 0 && j % 2 === 0) || (i % 2 !== 0 && j % 2 !== 0)) {
				color = colors.lighterGrayFill;
			} else {
				color = colors.lightGrayFill;
			}
			ctx.fillStyle = color;
			ctx.fillRect(rect.x, rect.y, rect.w, rect.h);

			// Draw circles
			const originalGlobalAlpha = ctx.globalAlpha;
			ctx.globalAlpha = pathTransparency;
			ctx.fillStyle = pathColor;
			if (isStart) {
				ctx
					.beginPath()
					.arc(center.x, center.y, squareSize * radiusRatio, 0, 2 * Math.PI)
					.fill();
			}
			ctx.globalAlpha = originalGlobalAlpha;
			ctx.restore();
		});
	});
	// Draw board border
	ctx.save();
	const lineWidth = ctx.lineWidth * 2.5;
	ctx.lineWidth = lineWidth;
	ctx.lineCap = "round";
	const rect = [
		boardMeasure.x - lineWidth / 2,
		boardMeasure.y - lineWidth / 2,
		boardMeasure.w + lineWidth,
		boardMeasure.h + lineWidth,
	];
	ctx.strokeRect(...rect);
	ctx.restore();

	//Draw Path
	if (path && path.length > 1) {
		ctx.save();
		ctx.lineWidth = ctx.lineWidth * 3.5;
		ctx.lineJoin = "round";
		ctx.globalAlpha = pathTransparency;
		ctx.strokeStyle = pathColor;
		for (let i = 1; i < path.length; ++i) {
			const c1 = Math2D.rectToArray(getSquareCenter(...path[i - 1]));
			const c2 = Math2D.rectToArray(getSquareCenter(...path[i]));
			const dxy = _.map([0, 1], dir => path[i][dir] - path[i - 1][dir]);
			const cirleOffset = squareSize * radiusRatio;
			if (i === 1) {
				_.each([0, 1], dir => {
					c1[dir] += dxy[dir] * cirleOffset * 0.95;
				});
				ctx.beginPath().moveTo(c1[0], c1[1]);
			}
			ctx.lineTo(c2[0], c2[1]);
			if (i === path.length - 1 && path.length === numCells) {
				const aSpan = squareSize / 4;
				const arrowEnd = _.map([0, 1], dir => {
					return c2[dir] + dxy[dir] * cirleOffset * 0.65;
				});
				ctx.lineTo(...arrowEnd);
				ctx.stroke();
				ctx.beginPath();
				ctx.lineCap = "round";
				ctx.lineJoin = "miter";
				ArrowDraw.drawStrokeArrow(ctx, {
					x1: c2[0],
					y1: c2[1],
					x2: arrowEnd[0],
					y2: arrowEnd[1],
					headHeight: aSpan * 1.5,
					headLength: aSpan * 1.75,
				});
			}
		}
		if (path.length !== numCells) {
			ctx.stroke();
		}
		ctx.restore();
	}

	// Draw Numbers
	_.each(grid, (row, i) => {
		_.each(row, (c, j) => {
			const numRect = getSquareRect(j, i);
			// Draw numbers
			if (!_.isNull(c) && c) {
				const isBold = _.findIndex(prefills, p => p[0] === c) !== -1;
				const batex =
					"$" + (isBold ? "\\bold{" : "") + c + (isBold ? "}" : "") + "$";
				const xpad = 0;
				const ypad = squareSize / 5;
				CanvasMath.drawInRect(ctx, {
					batex,
					rect: [
						numRect.x + xpad,
						numRect.y + ypad,
						numRect.w - 2 * xpad,
						numRect.h - 2 * ypad,
					],
				});
			}
		});
	});
};

module.exports = {
	generatePath,
	drawCheckerBoardPaths,
};
